import { makeVar, type ReactiveVar, useReactiveVar } from "@apollo/client";

import type { SavedJobState } from "../../../__generated__/types.gqlgenerated";

export type JobInteractionState = Readonly<{
  savedJobState?: SavedJobState;
  isReported?: boolean;
  isDisliked?: boolean;
}>;

const EMPTY_JOB_INTERACTION_STATE: JobInteractionState = Object.freeze({});

// One var per job. A single var for every job would rerender every JobCard, because Apollo
// reactive vars have no selectors.
const jobInteractionVars = new Map<string, ReactiveVar<JobInteractionState>>();
const publishedDislikeKeys = new Set<string>();
const dislikeSyncRevisionVar = makeVar(0);
const jobInteractionsResetRevisionVar = makeVar(0);
const reportJobDislikeFeedbackAutoOpenBlockedVar = makeVar(false);
const dislikeOperationTokens = new Map<string, number>();
const savedJobOperationTokens = new Map<string, number>();
const savedJobOperationPrevious = new Map<string, number>();
let nextOperationToken = 0;
let currentAccountId: string | undefined;

function beginOperation(tokens: Map<string, number>, jobKey: string): number {
  const token = ++nextOperationToken;
  tokens.set(jobKey, token);
  return token;
}

export function beginSavedJobOperation(jobKey: string): number {
  const previous = savedJobOperationTokens.get(jobKey);
  const token = beginOperation(savedJobOperationTokens, jobKey);
  if (previous === undefined) {
    savedJobOperationPrevious.delete(jobKey);
  } else {
    savedJobOperationPrevious.set(jobKey, previous);
  }
  return token;
}

export function cancelSavedJobOperation(jobKey: string, token: number): void {
  if (savedJobOperationTokens.get(jobKey) !== token) {
    return;
  }
  const previous = savedJobOperationPrevious.get(jobKey);
  if (previous === undefined) {
    savedJobOperationTokens.delete(jobKey);
  } else {
    savedJobOperationTokens.set(jobKey, previous);
  }
}

export function isCurrentSavedJobOperation(jobKey: string, token: number): boolean {
  return savedJobOperationTokens.get(jobKey) === token;
}

export function isCurrentDislikeOperation(jobKey: string, token: number): boolean {
  return dislikeOperationTokens.get(jobKey) === token;
}

function getJobInteractionVar(jobKey: string): ReactiveVar<JobInteractionState> {
  let jobInteractionVar = jobInteractionVars.get(jobKey);
  if (!jobInteractionVar) {
    jobInteractionVar = makeVar(EMPTY_JOB_INTERACTION_STATE);
    jobInteractionVars.set(jobKey, jobInteractionVar);
  }
  return jobInteractionVar;
}

export function useJobInteractionState(jobKey: string): JobInteractionState {
  return useReactiveVar(getJobInteractionVar(jobKey));
}

export function updateJobInteractionState(
  jobKey: string,
  update: Partial<JobInteractionState>,
): void {
  const jobInteractionVar = getJobInteractionVar(jobKey);
  jobInteractionVar({ ...jobInteractionVar(), ...update });
}

export function getSavedJobState(jobKey: string): SavedJobState | undefined {
  return getJobInteractionVar(jobKey)().savedJobState;
}

export function setSavedJobState(jobKey: string, savedJobState: SavedJobState | undefined): void {
  updateJobInteractionState(jobKey, { savedJobState });
}

// Does not replay the current value, and onNextChange fires once, so the listener re-arms.
// A dislike or report writes this same var, so an unchanged savedJobState is not delivered.
export function subscribeSavedJobState(
  jobKey: string,
  listener: (savedJobState: SavedJobState | undefined) => void,
): () => void {
  const jobInteractionVar = getJobInteractionVar(jobKey);
  let disposed = false;
  let cancelPending = () => {};
  let previousSavedJobState = jobInteractionVar().savedJobState;

  const arm = () => {
    if (disposed) {
      return;
    }
    cancelPending = jobInteractionVar.onNextChange((state) => {
      const nextSavedJobState = state.savedJobState;
      if (nextSavedJobState !== previousSavedJobState) {
        previousSavedJobState = nextSavedJobState;
        listener(nextSavedJobState);
      }
      arm();
    });
  };

  arm();

  return () => {
    disposed = true;
    cancelPending();
  };
}

export function getJobDisliked(jobKey: string): boolean | undefined {
  if (!publishedDislikeKeys.has(jobKey)) {
    return undefined;
  }
  return getJobInteractionVar(jobKey)().isDisliked;
}

export function setJobDisliked(jobKey: string, isDisliked: boolean): number {
  const token = beginOperation(dislikeOperationTokens, jobKey);
  publishedDislikeKeys.add(jobKey);
  updateJobInteractionState(jobKey, { isDisliked });
  dislikeSyncRevisionVar(dislikeSyncRevisionVar() + 1);
  return token;
}

export function useJobDislikeSyncRevision(): number {
  return useReactiveVar(dislikeSyncRevisionVar);
}

export function useJobInteractionsResetRevision(): number {
  return useReactiveVar(jobInteractionsResetRevisionVar);
}

// Keys with no publish stay as they are, so a dislike loaded from storage is not cleared.
export function applyPublishedDislikes(current: Set<string>): Set<string> {
  let changed = false;
  const next = new Set(current);
  for (const jobKey of publishedDislikeKeys) {
    const isDisliked = getJobDisliked(jobKey) === true;
    if (next.has(jobKey) === isDisliked) {
      continue;
    }
    changed = true;
    if (isDisliked) {
      next.add(jobKey);
    } else {
      next.delete(jobKey);
    }
  }
  return changed ? next : current;
}

export function useSavedJobState(
  jobKey: string,
  providerState: SavedJobState | undefined,
): SavedJobState | undefined {
  return useJobInteractionState(jobKey).savedJobState ?? providerState;
}

export function setJobReported(jobKey: string, isReported: boolean): void {
  updateJobInteractionState(jobKey, { isReported });
}

export function useIsJobReported(jobKey: string): boolean {
  return useJobInteractionState(jobKey).isReported === true;
}

/** Session-level state shared by report-job dislike feedback consumers in every RN root. */
export function useIsReportJobDislikeFeedbackAutoOpenBlocked(): boolean {
  return useReactiveVar(reportJobDislikeFeedbackAutoOpenBlockedVar);
}

export function setReportJobDislikeFeedbackAutoOpenBlocked(isBlocked: boolean): void {
  reportJobDislikeFeedbackAutoOpenBlockedVar(isBlocked);
}

/** Reset all mounted consumers without invalidating the reactive vars they subscribe to. */
export function resetJobInteractionsState(): void {
  publishedDislikeKeys.clear();
  dislikeOperationTokens.clear();
  savedJobOperationTokens.clear();
  savedJobOperationPrevious.clear();
  for (const jobInteractionVar of jobInteractionVars.values()) {
    jobInteractionVar(EMPTY_JOB_INTERACTION_STATE);
  }
  dislikeSyncRevisionVar(dislikeSyncRevisionVar() + 1);
  // Local sets do not observe a cleared publication set, so mounted surfaces watch this.
  jobInteractionsResetRevisionVar(jobInteractionsResetRevisionVar() + 1);
  reportJobDislikeFeedbackAutoOpenBlockedVar(false);
}

/** Reset user-specific facts when a different signed-in account mounts an RN provider. */
export function syncJobInteractionsAccount(accountId: string | null | undefined): void {
  // ProviderRoot wraps surfaces that do not all receive nativeContext. Missing context must not
  // clear another mounted provider's state; only an explicit null means signed out.
  if (accountId === undefined) return;
  if (accountId === null) {
    resetJobInteractionsForLogout();
    return;
  }
  if (currentAccountId !== undefined && currentAccountId !== accountId) {
    resetJobInteractionsState();
  }
  currentAccountId = accountId;
}

export function resetJobInteractionsForLogout(): void {
  resetJobInteractionsState();
  currentAccountId = undefined;
}
